import deepmerge from 'deepmerge';
import React, { createContext, useContext } from 'react';
import { CampaignTemplate } from '~/constants/vdp/campaignTemplate';
import type { DeepPartial } from '~/types';

export type CorrespondingEventIds = {
  searchRequestId?: string;
  vdpEventId?: string;
};

export type ContextType = {
  campaignTemplate: CampaignTemplate | null;
  correspondingEventIds: CorrespondingEventIds;
  isFromCreditKarma: boolean;
  utmParams: Record<string, string>;
};

const defaultContext: ContextType = {
  campaignTemplate: null,
  correspondingEventIds: {},
  isFromCreditKarma: false,
  utmParams: {}
};

const StaticContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<ContextType>;

export function StaticContextProvider({ children, ...props }: OwnProps) {
  return (
    <StaticContext.Provider value={props}>{children}</StaticContext.Provider>
  );
}

StaticContextProvider.displayName = 'StaticContextProvider';

export default StaticContext;

export const generateStaticContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedStaticContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <StaticContext.Provider value={generateStaticContext(mockValue)}>
      {children}
    </StaticContext.Provider>
  );
}

export function useCorrespondingSearchRequestId() {
  return useContext(StaticContext).correspondingEventIds.searchRequestId;
}

export function useIsFromCreditKarma() {
  return useContext(StaticContext).isFromCreditKarma;
}

export function useCampaignTemplate() {
  return useContext(StaticContext).campaignTemplate;
}

// Returns true for any active campaign template (ct=0, ct=1, etc.).
// Use for behaviors shared across all campaign variants (e.g. modal suppression).
// For ct=1 specific behavior, use useIsCampaignTemplateOne().
export function useIsCampaignTemplate() {
  const campaignTemplate = useCampaignTemplate();

  return campaignTemplate != null;
}

export function useIsCampaignTemplateOne() {
  return useContext(StaticContext).campaignTemplate === CampaignTemplate.One;
}

export function useIsCampaignTemplateTwo() {
  return useContext(StaticContext).campaignTemplate === CampaignTemplate.Two;
}

export function useIsCampaignTemplateThree() {
  return useContext(StaticContext).campaignTemplate === CampaignTemplate.Three;
}
